import type {ContractStatus, NewsSentiment, OpportunityStatus, RiskLevel} from "./supplier360DemoData";

/**
 * Compact currency formatter (e.g. €12.5M) using the supplier's own currency.
 * Self-contained so the cockpit does not depend on the profile currency.
 */
export function formatCompactCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatPercent(fraction: number, withSign = false): string {
    const pct = Math.round(fraction * 100);
    const sign = withSign && pct > 0 ? '+' : '';
    return `${sign}${pct}%`;
}

export function formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
}

/** MUI palette color name for a risk level. */
export function riskLevelColor(level: RiskLevel): 'success' | 'warning' | 'error' {
    switch (level) {
        case 'low':
            return 'success';
        case 'medium':
            return 'warning';
        case 'high':
            return 'error';
    }
}

/** Map a 0..100 risk score to a label + color. Higher score = riskier. */
export function riskScoreBand(score: number): { label: string; color: 'success' | 'warning' | 'error' } {
    if (score < 30) return {label: 'Low', color: 'success'};
    if (score < 50) return {label: 'Moderate', color: 'warning'};
    return {label: 'Elevated', color: 'error'};
}

export function contractStatusColor(status: ContractStatus): 'success' | 'warning' | 'error' | 'default' {
    switch (status) {
        case 'active':
            return 'success';
        case 'expiring':
            return 'warning';
        case 'expired':
            return 'error';
        case 'draft':
            return 'default';
    }
}

export function opportunityStatusColor(status: OpportunityStatus): 'info' | 'warning' | 'success' {
    switch (status) {
        case 'identified':
            return 'info';
        case 'in_progress':
            return 'warning';
        case 'realized':
            return 'success';
    }
}

export function opportunityStatusLabel(status: OpportunityStatus): string {
    switch (status) {
        case 'identified':
            return 'Identified';
        case 'in_progress':
            return 'In progress';
        case 'realized':
            return 'Realized';
    }
}

export function sentimentColor(sentiment: NewsSentiment): 'success' | 'default' | 'error' {
    switch (sentiment) {
        case 'positive':
            return 'success';
        case 'neutral':
            return 'default';
        case 'negative':
            return 'error';
    }
}

export function supplierStatusLabel(status: 'preferred' | 'approved' | 'under_review'): string {
    switch (status) {
        case 'preferred':
            return 'Preferred';
        case 'approved':
            return 'Approved';
        case 'under_review':
            return 'Under review';
    }
}

export function supplierStatusColor(status: 'preferred' | 'approved' | 'under_review'): 'success' | 'info' | 'warning' {
    switch (status) {
        case 'preferred':
            return 'success';
        case 'approved':
            return 'info';
        case 'under_review':
            return 'warning';
    }
}

export function transactionStatusColor(status: 'paid' | 'open' | 'overdue'): 'success' | 'info' | 'error' {
    switch (status) {
        case 'paid':
            return 'success';
        case 'open':
            return 'info';
        case 'overdue':
            return 'error';
    }
}
